import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import { useEffect, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { GitCommit, GitPullRequest, GitBranch, Users, MessageSquare, TrendingUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar } from "recharts";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Github, Gitlab, GitFork, ExternalLink } from "lucide-react";

const PLATFORM_ICON = { github: Github, gitlab: Gitlab, bitbucket: GitFork };

function Tooltip2({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-strong rounded-lg px-3 py-2 text-xs shadow-lg">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function GitAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [mappings, setMappings] = useState([]);

  useEffect(() => {
    api.get(`/git/overview?days=${days}`).then((r) => setData(r.data));
    api.get(`/git/mappings?unmapped_only=true`).then((r) => setMappings(r.data)).catch(() => {});
  }, [days]);

  if (!data) return <div className="p-8 text-muted-foreground">Loading git analytics…</div>;

  const t = data.totals;

  return (
    <div className="space-y-6">
      <PageHeader
        testid="git-header"
        title="Git Analytics"
        subtitle="Cross-platform engineering telemetry"
        actions={
          <div className="flex gap-2">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-36" data-testid="git-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button asChild variant="outline"><Link to="/repositories"><GitBranch className="w-4 h-4 mr-2" />Repositories</Link></Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="Repositories" value={t.repositories} icon={GitBranch} tone="primary" />
        <KpiCard label="Commits" value={t.commits} icon={GitCommit} />
        <KpiCard label="Pull Requests" value={t.pull_requests} icon={GitPullRequest} />
        <KpiCard label="Reviews" value={t.reviews} icon={MessageSquare} />
        <KpiCard label="Active Devs" value={t.contributors} icon={Users} tone="success" />
      </div>

      <SectionCard title="Activity Trend" subtitle={`Last ${days} days`}>
        <div className="h-72">
          <ResponsiveContainer>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip content={<Tooltip2 />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="commits" stroke="hsl(211 100% 55%)" strokeWidth={2.5} dot={false} name="Commits" />
              <Line type="monotone" dataKey="prs" stroke="hsl(262 83% 62%)" strokeWidth={2.5} dot={false} name="Pull Requests" />
              <Line type="monotone" dataKey="reviews" stroke="hsl(32 95% 55%)" strokeWidth={2.5} dot={false} name="Reviews" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>

      <div className="grid lg:grid-cols-3 gap-6">
        <SectionCard title="Top Contributors" subtitle={`By commits (last ${days}d)`} className="lg:col-span-2">
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={data.top_contributors.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip content={<Tooltip2 />} />
                <Bar dataKey="commits" fill="hsl(211 100% 55%)" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Contributor Detail">
          <div className="space-y-2 max-h-72 overflow-y-auto scroll-thin">
            {data.top_contributors.map((c, i) => (
              <div key={c.id + i} className="flex items-center gap-3 py-1.5" data-testid={`contrib-${i}`}>
                <div className="number w-6 text-center text-[11px] text-muted-foreground">#{i + 1}</div>
                <Avatar className="h-8 w-8">
                  <AvatarImage src={authedUrl(c.avatar_url)} />
                  <AvatarFallback className="text-[10px]">{(c.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {c.mapped ? (c.name) : (
                      <>
                        <span className="truncate">{c.name}</span>
                        <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-500">Unmapped</Badge>
                      </>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate">{c.department || "external"}</div>
                </div>
                <div className="text-right">
                  <div className="number text-sm">{c.commits}</div>
                  <div className="text-[9px] text-muted-foreground">commits</div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Repositories" subtitle="Sync status per repo">
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.repositories.map((r) => {
            const Icon = PLATFORM_ICON[r.platform] || Github;
            return (
              <div key={r.id} data-testid={`repo-sum-${r.id}`} className="rounded-lg hairline p-3 flex items-center gap-3">
                <Icon className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {r.commits} commits · {r.prs} PRs
                  </div>
                </div>
                <Badge variant={r.sync_status === "success" ? "default" : "outline"} className="text-[10px] capitalize">{r.sync_status || "idle"}</Badge>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {mappings.length > 0 && (
        <SectionCard
          title="Unmapped Contributors"
          subtitle="These git identities are not linked to any employee"
          actions={<Button asChild variant="outline" size="sm"><Link to="/repositories">Manage</Link></Button>}
        >
          <div className="grid md:grid-cols-2 gap-2">
            {mappings.slice(0, 6).map((m) => (
              <div key={m.git_email} className="flex items-center gap-3 p-3 rounded-lg hairline">
                <div className="h-8 w-8 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500 text-xs">?</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{m.display_name || m.git_email}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{m.git_email} · {m.commit_count} commits</div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
